import { CellStatus } from "@/queries/smart-tables/types";
import {
  formatExecutionMetadataLatency,
  formatExecutionMetadataPrice,
  toFiniteExecutionMetricNumber,
} from "@/routes/smart-table/utils/executionMetadataFormatting";
import { DEFAULT_CELL_ERROR_MESSAGE } from "@/routes/smart-table/utils/cellDisplayState";
import {
  isMultiContentValue,
  isOutputMediaItem,
  isOutputMediaItemArray,
  summarizeMultiContent,
  type MultiContentValue,
  type OutputMediaItem,
} from "@/routes/smart-table/utils/multiContent";

// ── Enums ─────────────────────────────────────────────────────────────────────

export enum CellContentType {
  PLAIN_TEXT = "plain_text",
  MARKDOWN = "markdown",
  JSON = "json",
  LINK = "link",
  IMAGE = "image",
  AUDIO = "audio",
  VIDEO = "video",
  PDF = "pdf",
  MEDIA_COLLECTION = "media_collection",
  MULTI_CONTENT = "multi_content",
  FILE = "file",
  PRICE = "price",
  DURATION = "duration",
  ERROR = "error",
  EMPTY = "empty",
}

// ── Interfaces ─────────────────────────────────────────────────────────────────

export type MediaPayload = OutputMediaItem;
export type LinkPreviewKind = "image" | "video" | "audio" | "pdf" | "link";

export interface LinkPayload {
  url: string;
  label: string;
  kind: LinkPreviewKind;
}

export interface ExecutionMetricPayload {
  label: string;
  formattedValue: string;
  rawNumber: number | null;
  unit: "USD" | "ms";
}

export interface ClassifiedContent {
  /** The detected content type. */
  type: CellContentType;
  /** Short text suitable for inline cell display (truncated preview). */
  displayPreview: string;
  /** Original, unparsed cell value. */
  rawValue: unknown;
  /** Parsed / structured value (e.g. a JS object for JSON content). */
  parsedValue: unknown;
  /**
   * True for JSON, Markdown, Media, Error — types that always benefit from
   * the rich dialog regardless of overflow.
   * For PLAIN_TEXT, expand-icon visibility is determined by DOM overflow.
   */
  isStructuredType: boolean;
  /** Populated for media content types only. */
  mediaPayload?: MediaPayload | MediaPayload[];
  multiContentPayload?: MultiContentValue;
  /** Populated for standalone public URL content only. */
  linkPayload?: LinkPayload;
  /** Populated for execution metric content only. */
  metricPayload?: ExecutionMetricPayload;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

export const CLASSIFY_SCAN_LIMIT = 8_192;
export const CLASSIFIED_CONTENT_PREVIEW_LIMIT = 200;

export const capClassifiedDisplayPreview = (value: string): string =>
  value.slice(0, CLASSIFIED_CONTENT_PREVIEW_LIMIT);

const getFirstNonWhitespaceChar = (
  value: string,
  maxScanLength: number = value.length,
): string | undefined => {
  const scanLength = Math.min(value.length, maxScanLength);
  for (let index = 0; index < scanLength; index += 1) {
    const char = value[index];
    if (char && !/\s/.test(char)) {
      return char;
    }
  }

  return undefined;
};

const getLastNonWhitespaceChar = (
  value: string,
  maxScanLength: number = value.length,
): string | undefined => {
  const minIndex = Math.max(0, value.length - maxScanLength);
  for (let index = value.length - 1; index >= minIndex; index -= 1) {
    const char = value[index];
    if (char && !/\s/.test(char)) {
      return char;
    }
  }

  return undefined;
};

const canParseJsonObjectOrArray = (value: string): boolean => {
  const firstChar = getFirstNonWhitespaceChar(value, CLASSIFY_SCAN_LIMIT);
  return firstChar === "{" || firstChar === "[";
};

const hasJsonLikeContainer = (value: string): boolean => {
  const firstChar = getFirstNonWhitespaceChar(value, CLASSIFY_SCAN_LIMIT);
  const lastChar = getLastNonWhitespaceChar(value, CLASSIFY_SCAN_LIMIT);

  return (firstChar === "{" && lastChar === "}") || (firstChar === "[" && lastChar === "]");
};

const hasOversizedStringValue = (
  value: unknown,
  seen: WeakSet<object> = new WeakSet(),
): boolean => {
  if (typeof value === "string") {
    return value.length > CLASSIFY_SCAN_LIMIT;
  }

  if (value === null || typeof value !== "object") {
    return false;
  }

  if (seen.has(value)) {
    return false;
  }
  seen.add(value);

  if (Array.isArray(value)) {
    return value.some(item => hasOversizedStringValue(item, seen));
  }

  return Object.values(value as Record<string, unknown>).some(item =>
    hasOversizedStringValue(item, seen),
  );
};

/**
 * Parses a string that may be a Python-dict-style value (single quotes, True/False/None)
 * into a JS object. Returns the original string if not parseable as a non-null object.
 */
export function parsePythonishValue(raw: string): unknown {
  // Attempt 1: Safe regex replacement of Python-style single-quoted strings
  try {
    const safe = raw
      .replace(/(^|[[{:\s,])'(.*?)'(?=[\]}\s,]|$)/g, '$1"$2"')
      .replace(/\bTrue\b/g, "true")
      .replace(/\bFalse\b/g, "false")
      .replace(/\bNone\b/g, "null");
    const parsed = JSON.parse(safe);
    if (typeof parsed === "object" && parsed !== null) {
      return parsed;
    }
  } catch {
    // fall through
  }

  // Attempt 2: Aggressive single-quote replacement
  try {
    const aggressive = raw
      .replace(/'/g, '"')
      .replace(/\bTrue\b/g, "true")
      .replace(/\bFalse\b/g, "false")
      .replace(/\bNone\b/g, "null");
    const parsed = JSON.parse(aggressive);
    if (typeof parsed === "object" && parsed !== null) {
      return parsed;
    }
  } catch {
    // fall through
  }

  return raw;
}

// Markdown detection indicators — requires 2+ to avoid false positives
const STANDALONE_PUBLIC_URL_PATTERN = /^(https?:\/\/|\/\/)/i;

const URL_PREVIEW_PATTERNS: [LinkPreviewKind, RegExp][] = [
  ["image", /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico|tiff|tif)(\?|#|$)/i],
  ["video", /\.(mp4|avi|mov|wmv|flv|webm|mkv|m4v|3gp|ogv)(\?|#|$)/i],
  ["audio", /\.(mp3|wav|ogg|aac|flac|m4a|wma|opus)(\?|#|$)/i],
  ["pdf", /\.(pdf)(\?|#|$)/i],
];

export function isStandalonePublicUrl(value: string): boolean {
  const trimmed = value.trim();

  return trimmed.length > 0 && !/\s/.test(trimmed) && STANDALONE_PUBLIC_URL_PATTERN.test(trimmed);
}

export function getPublicUrlPreviewKind(url: string): LinkPreviewKind {
  const normalizedUrl = url.trim();
  const matched = URL_PREVIEW_PATTERNS.find(([, pattern]) => pattern.test(normalizedUrl));
  return matched?.[0] ?? "link";
}

export function getPublicUrlLabel(url: string): string {
  const trimmed = url.trim();

  try {
    const parsed = trimmed.startsWith("//") ? new URL(`https:${trimmed}`) : new URL(trimmed);
    const filename = parsed.pathname.split("/").filter(Boolean).pop();
    return filename ? decodeURIComponent(filename) : parsed.hostname || trimmed;
  } catch {
    const withoutQuery = trimmed.split(/[?#]/)[0] ?? trimmed;
    const filename = withoutQuery.split("/").filter(Boolean).pop();
    return filename ? decodeURIComponent(filename) : trimmed;
  }
}

export function buildPublicUrlLinkPayload(value: string): LinkPayload {
  const url = value.trim();

  return {
    url,
    label: getPublicUrlLabel(url),
    kind: getPublicUrlPreviewKind(url),
  };
}

function classifyPublicUrl(rawValue: string): ClassifiedContent {
  const linkPayload = buildPublicUrlLinkPayload(rawValue);

  return {
    type: CellContentType.LINK,
    displayPreview: capClassifiedDisplayPreview(linkPayload.label),
    rawValue,
    parsedValue: linkPayload,
    isStructuredType: true,
    linkPayload,
  };
}

const MARKDOWN_INDICATORS: RegExp[] = [
  /^#{1,6}\s/m, // Headings
  /\*\*[^*]+\*\*/, // Bold
  /\*[^*]+\*/, // Italic
  /```[\s\S]*?```/, // Code fences
  /\[.+?\]\(.+?\)/, // Links
  /^[-*+]\s/m, // Unordered list items
  /^\d+\.\s/m, // Ordered list items
  /^\|.*\|.*\|/m, // Table rows
  /^>\s/m, // Blockquotes
  /^---$/m, // Horizontal rules
  /- \[[ x]\]/, // Task lists
];

const LOOKS_LIKE_MARKDOWN_CACHE = new Map<string, boolean>();
const MARKDOWN_CACHE_LIMIT = 1_000;
const MAX_CACHEABLE_MARKDOWN_TEXT_LENGTH = 4_096;

const canCacheMarkdownCheck = (text: string): boolean =>
  text.length <= MAX_CACHEABLE_MARKDOWN_TEXT_LENGTH;

const setMarkdownCacheValue = (text: string, value: boolean): boolean => {
  if (LOOKS_LIKE_MARKDOWN_CACHE.size >= MARKDOWN_CACHE_LIMIT) {
    LOOKS_LIKE_MARKDOWN_CACHE.clear();
  }
  LOOKS_LIKE_MARKDOWN_CACHE.set(text, value);
  return value;
};

export interface ParsedMarkdownCodeFence {
  code: string;
  language: string;
}

export function parseMarkdownCodeFence(text: string): ParsedMarkdownCodeFence | null {
  const scanText = text.length > CLASSIFY_SCAN_LIMIT ? text.slice(0, CLASSIFY_SCAN_LIMIT) : text;
  const fenceMatch = scanText.match(/^\s*```([^\n`]*)\n?([\s\S]*?)```\s*$/);
  if (!fenceMatch) {
    return null;
  }

  return {
    language: fenceMatch[1]?.trim().split(/\s+/)[0]?.toLowerCase() ?? "",
    code: (fenceMatch[2] ?? "").trim(),
  };
}

/**
 * Returns true if the text contains 2+ markdown indicators.
 * Conservative threshold to avoid false positives on plain prose.
 */
export function looksLikeMarkdown(text: string): boolean {
  const scanText = text.length > CLASSIFY_SCAN_LIMIT ? text.slice(0, CLASSIFY_SCAN_LIMIT) : text;
  const shouldCache = canCacheMarkdownCheck(scanText);

  if (shouldCache) {
    const cached = LOOKS_LIKE_MARKDOWN_CACHE.get(scanText);
    if (cached !== undefined) {
      return cached;
    }
  }

  if (parseMarkdownCodeFence(scanText)) {
    return shouldCache ? setMarkdownCacheValue(scanText, true) : true;
  }

  let matches = 0;
  for (const pattern of MARKDOWN_INDICATORS) {
    if (pattern.test(scanText)) {
      matches += 1;
      if (matches >= 2) {
        return shouldCache ? setMarkdownCacheValue(scanText, true) : true;
      }
    }
  }
  return shouldCache ? setMarkdownCacheValue(scanText, false) : false;
}

/**
 * Strips markdown syntax for a clean single-line cell preview.
 * Preserves the textual content while removing formatting markers.
 */
export function stripMarkdownForPreview(
  text: string,
  maxLength: number = MAX_CACHEABLE_MARKDOWN_TEXT_LENGTH,
): string {
  return text
    .slice(0, maxLength)
    .replace(/```[^\n`]*\n?([\s\S]*?)```/g, "$1") // Code fences → code content
    .replace(/^#{1,6}\s+/gm, "") // Headings markers
    .replace(/\*\*([^*]+)\*\*/g, "$1") // Bold → content
    .replace(/__([^_]+)__/g, "$1") // Bold alt → content
    .replace(/\*([^*]+)\*/g, "$1") // Italic → content
    .replace(/_([^_]+)_/g, "$1") // Italic alt → content
    .replace(/~~([^~]+)~~/g, "$1") // Strikethrough → content
    .replace(/`([^`]+)`/g, "$1") // Inline code → content
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // Links → text
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1") // Images → alt text
    .replace(/^[-*+]\s+/gm, "") // Unordered list markers
    .replace(/^\d+\.\s+/gm, "") // Ordered list markers
    .replace(/^>\s+/gm, "") // Blockquote markers
    .replace(/^---$/gm, "") // Horizontal rules
    .replace(/- \[[ x]\]\s*/g, "") // Task list markers
    .replace(/\|/g, " ") // Table pipes → space
    .replace(/\n+/g, " ") // All newlines → space
    .replace(/\s{2,}/g, " ") // Collapse multiple spaces
    .trim();
}

// ── Main Classifier ────────────────────────────────────────────────────────────

/**
 * Normalizes a cell's raw backend value + display_value into the single input
 * that classifyContent expects.
 *
 * This mirrors the logic in SheetDataStore._buildRowData:
 *   - Media cells (output_media object or array) → use cellValue (the structured object)
 *   - Everything else → use displayValue (the human-readable string)
 *
 * Use this in the dialog (which reads cell.value directly from MobX) so that
 * computed-column cells whose cell.value is an internal wrapper object are
 * correctly classified as PLAIN_TEXT / MARKDOWN / JSON based on their
 * display_value string, not their internal value structure.
 */
export function normalizeForClassification(
  cellValue: unknown,
  displayValue: string | null | undefined,
): unknown {
  if (cellValue !== null && typeof cellValue === "object") {
    if (
      isOutputMediaItem(cellValue) ||
      isOutputMediaItemArray(cellValue) ||
      isMultiContentValue(cellValue)
    ) {
      return cellValue;
    }
  }

  // For all non-structured values, use the human-readable display_value.
  // This prevents computed-column wrapper objects like { value: "text", tokens: 150 }
  // from being misclassified as JSON.
  return displayValue ?? null;
}

// ── Media-from-object helper ───────────────────────────────────────────────
//
// Shared by both the direct-object path (step 4) and the parsed-string path
// (step 6). When display_value is a JSON *string* like
//   '{"type":"output_media","media_type":"image/png","url":"…"}'
// the old parseRawDisplay would JSON.parse it first, then detect media.
// classifyContent must do the same — after parsing a string into an object,
// check whether that object is actually a media payload before falling back
// to CellContentType.JSON.

function classifyMediaObject(
  obj: Record<string, unknown>,
  rawValue: unknown,
): ClassifiedContent | null {
  if (isOutputMediaItem(obj)) {
    const media = obj;
    const mt = media.media_type ?? "";
    let type: CellContentType;

    if (mt.startsWith("image")) {
      type = CellContentType.IMAGE;
    } else if (mt.startsWith("video")) {
      type = CellContentType.VIDEO;
    } else if (mt.startsWith("audio")) {
      type = CellContentType.AUDIO;
    } else if (mt === "pdf" || mt === "application/pdf") {
      type = CellContentType.PDF;
    } else {
      type = CellContentType.FILE;
    }

    return {
      type,
      displayPreview: capClassifiedDisplayPreview(media.title ?? "Media File"),
      rawValue,
      parsedValue: media,
      isStructuredType: true,
      mediaPayload: media,
    };
  }
  return null;
}

function classifyMediaArray(arr: unknown[], rawValue: unknown): ClassifiedContent | null {
  if (isOutputMediaItemArray(arr)) {
    const items = arr;
    return {
      type: CellContentType.MEDIA_COLLECTION,
      displayPreview: `${items.length} files`,
      rawValue,
      parsedValue: items,
      isStructuredType: true,
      mediaPayload: items,
    };
  }
  return null;
}

/**
 * If `obj` is an output_media object/array, return a ClassifiedContent for it.
 * Otherwise return null so the caller can fall back to JSON / other types.
 */
function tryClassifyAsMedia(obj: object, rawValue: unknown): ClassifiedContent | null {
  if (Array.isArray(obj)) {
    return classifyMediaArray(obj, rawValue);
  }
  return classifyMediaObject(obj as Record<string, unknown>, rawValue);
}

function tryClassifyAsMultiContent(obj: unknown, rawValue: unknown): ClassifiedContent | null {
  if (!isMultiContentValue(obj)) {
    return null;
  }

  return {
    type: CellContentType.MULTI_CONTENT,
    displayPreview: capClassifiedDisplayPreview(summarizeMultiContent(obj)),
    rawValue,
    parsedValue: obj,
    isStructuredType: true,
    multiContentPayload: obj,
  };
}

export function classifyExecutionMetricContent(
  rawValue: unknown,
  displayValue: string | null | undefined,
  metricKey: string,
): ClassifiedContent | null {
  let metricType: CellContentType.PRICE | CellContentType.DURATION | null = null;
  if (metricKey === "price") {
    metricType = CellContentType.PRICE;
  } else if (metricKey === "latency_ms") {
    metricType = CellContentType.DURATION;
  }

  if (!metricType) {
    return null;
  }

  const rawNumber = toFiniteExecutionMetricNumber(rawValue);
  const formattedValue =
    metricType === CellContentType.PRICE
      ? formatExecutionMetadataPrice(rawValue)
      : formatExecutionMetadataLatency(rawValue);
  const displayPreview = capClassifiedDisplayPreview(formattedValue || displayValue || "");

  return {
    type: metricType,
    displayPreview,
    rawValue,
    parsedValue: rawValue,
    isStructuredType: true,
    metricPayload: {
      label: metricType === CellContentType.PRICE ? "Price" : "Duration",
      formattedValue: displayPreview,
      rawNumber,
      unit: metricType === CellContentType.PRICE ? "USD" : "ms",
    },
  };
}

/**
 * Classifies arbitrary cell content into a typed `ClassifiedContent` object.
 *
 * Detection priority (first match wins):
 *  1. FAILED status → ERROR
 *  2. STALE + errorMessage → ERROR
 *  3. null / undefined / "" → EMPTY
 *  4. object/array with type "output_media" → IMAGE / VIDEO / AUDIO / PDF / FILE / MEDIA_COLLECTION
 *  5. string → JSON parse → if parsed is media → media type; else → JSON
 *                          → pythonish parse → same
 *                          → looksLikeMarkdown → MARKDOWN
 *                          → PLAIN_TEXT
 *  6. non-null object (already parsed, not media) → JSON
 *  7. fallback → PLAIN_TEXT
 */
export function classifyContent(
  rawValue: unknown,
  displayValue?: string | null,
  cellStatus?: CellStatus,
  errorMessage?: string,
): ClassifiedContent {
  // ── 1 & 2. Error states ────────────────────────────────────────────────────
  if (cellStatus === CellStatus.FAILED || (cellStatus === CellStatus.STALE && !!errorMessage)) {
    const errText = errorMessage ?? DEFAULT_CELL_ERROR_MESSAGE;
    return {
      type: CellContentType.ERROR,
      displayPreview: capClassifiedDisplayPreview(errText),
      rawValue,
      parsedValue: rawValue,
      isStructuredType: true,
    };
  }

  // ── 3. Empty ───────────────────────────────────────────────────────────────
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return {
      type: CellContentType.EMPTY,
      displayPreview: "",
      rawValue,
      parsedValue: rawValue,
      isStructuredType: false,
    };
  }

  // ── 4. Direct object/array — check multi-content/media first, then JSON ───
  if (typeof rawValue === "object" && rawValue !== null) {
    const multi = tryClassifyAsMultiContent(rawValue, rawValue);
    if (multi) {
      return multi;
    }

    const media = tryClassifyAsMedia(rawValue, rawValue);
    if (media) {
      return media;
    }

    // Not media/multi-content → classify as JSON
    let preview: string;
    if (hasOversizedStringValue(rawValue)) {
      preview = "[Large structured value]";
    } else {
      try {
        preview = JSON.stringify(rawValue);
      } catch {
        preview = String(rawValue);
      }
    }
    return {
      type: CellContentType.JSON,
      displayPreview: capClassifiedDisplayPreview(preview),
      rawValue,
      parsedValue: rawValue,
      isStructuredType: true,
    };
  }

  // ── 5. String — parse, then check media, then JSON, then markdown/text ────
  if (typeof rawValue === "string") {
    const isOverScanLimit = rawValue.length > CLASSIFY_SCAN_LIMIT;
    const scanValue = isOverScanLimit ? rawValue.slice(0, CLASSIFY_SCAN_LIMIT) : rawValue;
    let parsed: unknown = rawValue;

    if (canParseJsonObjectOrArray(rawValue)) {
      if (isOverScanLimit) {
        if (hasJsonLikeContainer(rawValue)) {
          return {
            type: CellContentType.JSON,
            displayPreview: capClassifiedDisplayPreview(displayValue ?? rawValue),
            rawValue,
            parsedValue: undefined,
            isStructuredType: true,
          };
        }
      } else {
        // Attempt standard JSON parse
        try {
          parsed = JSON.parse(rawValue);
        } catch {
          // Attempt pythonish dict parse
          parsed = parsePythonishValue(rawValue);
        }
      }
    }

    // If parsing succeeded, prefer JSON over markdown.
    if (parsed !== rawValue) {
      if (typeof parsed === "object" && parsed !== null) {
        const multi = tryClassifyAsMultiContent(parsed, rawValue);
        if (multi) {
          return multi;
        }

        const media = tryClassifyAsMedia(parsed, rawValue);
        if (media) {
          return media;
        }
      }

      return {
        type: CellContentType.JSON,
        displayPreview: capClassifiedDisplayPreview(displayValue ?? rawValue),
        rawValue,
        parsedValue: parsed,
        isStructuredType: true,
      };
    }

    if (!isOverScanLimit && isStandalonePublicUrl(rawValue)) {
      return classifyPublicUrl(rawValue);
    }

    // Check for markdown
    if (looksLikeMarkdown(scanValue)) {
      return {
        type: CellContentType.MARKDOWN,
        displayPreview: capClassifiedDisplayPreview(displayValue ?? rawValue),
        rawValue,
        parsedValue: rawValue,
        isStructuredType: true,
      };
    }

    // Plain text
    return {
      type: CellContentType.PLAIN_TEXT,
      displayPreview: capClassifiedDisplayPreview(displayValue ?? rawValue),
      rawValue,
      parsedValue: rawValue,
      isStructuredType: false,
    };
  }

  // ── 6. Fallback ────────────────────────────────────────────────────────────
  return {
    type: CellContentType.PLAIN_TEXT,
    displayPreview: capClassifiedDisplayPreview(displayValue ?? String(rawValue)),
    rawValue,
    parsedValue: rawValue,
    isStructuredType: false,
  };
}

export const stringifyRawCellContent = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const getSingleMediaCopyText = (media: MediaPayload): string => {
  if (typeof media.url === "string" && media.url.length > 0) {
    return media.url;
  }

  if (typeof media.storage_key === "string" && media.storage_key.length > 0) {
    return media.storage_key;
  }

  return stringifyRawCellContent(media);
};

export function getCopyTextForClassifiedContent(content: ClassifiedContent): string {
  switch (content.type) {
    case CellContentType.PLAIN_TEXT:
    case CellContentType.MARKDOWN:
      return typeof content.rawValue === "string"
        ? content.rawValue
        : (content.displayPreview ?? "");
    case CellContentType.JSON:
      return stringifyRawCellContent(content.parsedValue ?? content.rawValue);
    case CellContentType.LINK:
      return content.linkPayload?.url ?? stringifyRawCellContent(content.rawValue);
    case CellContentType.PRICE:
    case CellContentType.DURATION:
      return content.metricPayload?.formattedValue ?? content.displayPreview;
    case CellContentType.IMAGE:
    case CellContentType.AUDIO:
    case CellContentType.VIDEO:
    case CellContentType.PDF:
    case CellContentType.FILE:
      return content.mediaPayload && !Array.isArray(content.mediaPayload)
        ? getSingleMediaCopyText(content.mediaPayload)
        : stringifyRawCellContent(content.rawValue);
    case CellContentType.MEDIA_COLLECTION:
      if (Array.isArray(content.mediaPayload)) {
        return content.mediaPayload.map(getSingleMediaCopyText).join("\n");
      }
      return stringifyRawCellContent(content.rawValue);
    case CellContentType.MULTI_CONTENT:
      if (content.multiContentPayload) {
        return content.multiContentPayload.items
          .map(item => {
            if (item.type === "text") {
              return item.text;
            }
            if (item.type === "json") {
              return stringifyRawCellContent(item.value);
            }
            return getSingleMediaCopyText(item);
          })
          .filter(segment => segment.length > 0)
          .join("\n\n");
      }
      return stringifyRawCellContent(content.rawValue);
    case CellContentType.ERROR:
      return content.displayPreview;
    case CellContentType.EMPTY:
      return "";
    default:
      return stringifyRawCellContent(content.rawValue);
  }
}
